import { bigDealAmount } from "../Dashboard/constants";
import { byId } from "../Dashboard/animations/registry";

// De rekenkant van het animatielab. Elke animatie hoort bij iets wat er in
// het echt gebeurt, dus in het lab verandert hier ook echt een bedrag: de
// balk groeit, de bedragen tellen op, de ranglijst schuift. Zonder dat kijk
// je naar een plaatje dat over een stilstaand scherm heen beweegt.

const pick = (list) => list[Math.floor(Math.random() * list.length)];

// Een bedrag dat er als een echte deal uitziet: hele vijftigtallen.
const roundDeal = (min, max) =>
  Math.round((min + Math.random() * (max - min)) / 50) * 50;

const bySize = (a, b) => (b.totalPrice || 0) - (a.totalPrice || 0);

// Telt een bedrag op bij een naam in een lijst en zet de lijst weer op
// volgorde. De lijst zelf blijft ongemoeid: het dashboard vergelijkt oud met
// nieuw, dus we mogen niets ter plekke aanpassen.
function addTo(list, name, amount) {
  const found = list.some((row) => row.name === name);
  const next = found
    ? list.map((row) =>
        row.name === name
          ? { ...row, totalPrice: (row.totalPrice || 0) + amount }
          : row,
      )
    : [...list, { name, totalPrice: amount }];

  return next.sort(bySize);
}

// Verwerkt één verkoop in alle cijfers die het dashboard laat zien.
export function applySale(data, { name, service, amount }) {
  const rankList = addTo(data.rankList, name, amount);
  const soldServices = service
    ? addTo(data.soldServices, service, amount)
    : data.soldServices;

  const total = data.amount + amount;
  const target = data.teamTarget.amount || 0;

  // De bovenste rij van de jaarlijst is de lopende maand; die hoort mee te
  // bewegen als er iemand anders bovenaan komt te staan.
  const leader = rankList[0];
  const yearRankList = data.yearRankList.length
    ? [
        {
          ...data.yearRankList[0],
          name: leader.name,
          totalPrice: leader.totalPrice,
        },
        ...data.yearRankList.slice(1),
      ]
    : data.yearRankList;

  return {
    ...data,
    rankList,
    soldServices,
    yearRankList,
    amount: total,
    scoredTowardsTarget: target ? Math.min(target, total) : total,
  };
}

// De negen cijfertoetsen van het lab: drie verkopers, drie bedragen.
// Toets 1..3 is Ernst, 4..6 Bobbie, 7..9 Bert. Zo weet je van tevoren wat
// er gaat gebeuren: een klein bedrag, een deal vanaf mediumDealAmount (een
// tweede bui confetti) en een deal vanaf bigDealAmount (een derde).
export const labSellers = ["Ernst", "Bobbie", "Bert"];
export const labAmounts = [100, 500, 1100];

export const payments = labSellers.flatMap((name, row) =>
  labAmounts.map((amount, column) => ({
    key: String(row * 3 + column + 1),
    name,
    amount,
  })),
);

// Een betaling van de cijfertoetsen. De dienst wisselt, zodat de kolom
// "Verkochte diensten" ook meebeweegt.
export function paymentSale(data, payment) {
  return {
    name: payment.name,
    service: pick(data.soldServices).name,
    amount: payment.amount,
  };
}

// Het record en de eigen records waar het lab mee rekent. Niet die uit
// localStorage: dat zijn de echte van de tv, en die mag het lab niet lezen
// (dan gaat er nooit iets af) en al helemaal niet ophogen. De bedragen zijn
// zo gekozen dat de scenario's hieronder precies doen wat erbij staat.
export const labRecord = { name: "Bobbie", amount: 5200 };
export const labBests = {
  Ernst: { amount: 2400 },
  Bobbie: { amount: 5200 },
  Bert: { amount: 4000 },
  Ernie: { amount: 2600 },
  Hans: { amount: 1600 },
  Grietje: { amount: 1200 },
  "Johan Derksen": { amount: 1500 },
};

// Na een verkoop de lat ophogen, zoals useSaleEvents dat live doet: wat er
// per verkoper bij kwam is een losse verkoop, en is die groter dan wat we
// kenden, dan is dat de nieuwe lat.
export function raiseRecords(before, next, { record, bests }) {
  const was = new Map(before.rankList.map((row) => [row.name, row.totalPrice || 0]));
  let nextRecord = record;
  const nextBests = { ...bests };

  next.rankList.forEach(({ name, totalPrice }) => {
    const amount = (totalPrice || 0) - (was.get(name) ?? 0);
    if (amount <= 0) return;
    if (amount > (nextRecord?.amount ?? 0)) nextRecord = { name, amount };
    if (amount > (nextBests[name]?.amount ?? 0)) nextBests[name] = { amount };
  });

  return { record: nextRecord, bests: nextBests };
}

// De maand is bijna rond: iedereen evenredig opgehoogd tot het team op
// 'short' euro van het target staat. De volgorde blijft zo gelijk, en de
// meter blijft onder de 100 (useSaleEvents rondt het percentage af, dus niet
// te krap: op 99,5% telt hij al als gehaald).
export function nearTarget(data, short = 800) {
  const target = data.teamTarget.amount || 0;
  const total = target - short;
  if (!target || total <= data.amount) return data;

  const factor = total / data.amount;
  const scale = (row) => ({
    ...row,
    totalPrice: Math.round(((row.totalPrice || 0) * factor) / 50) * 50,
  });
  const rankList = data.rankList.map(scale);
  rankList[0].totalPrice += total - rankList.reduce((sum, row) => sum + row.totalPrice, 0);

  const yearRankList = data.yearRankList.length
    ? [{ ...data.yearRankList[0], ...rankList[0] }, ...data.yearRankList.slice(1)]
    : data.yearRankList;

  return {
    ...data,
    rankList,
    soldServices: data.soldServices.map(scale),
    yearRankList,
    amount: total,
    scoredTowardsTarget: total,
  };
}

// Wat er in het echt kan gebeuren, van begin tot eind. Elk scenario begint
// bij de begincijfers en komt binnen als betaling: het dashboard bepaalt
// daarna zelf wat erbij hoort, met labRecord en labBests als lat. 'steps' is
// wat je dan hoort te zien; klopt dat niet, dan zit de fout in het dashboard.
//
// sales      - de verkopen, allemaal in dezelfde ronde ophalen
// nearTarget - eerst stil naar vlak onder het target, dan pas de verkoop
// animation  - geen verkoop maar een animatie die zelf iets vertelt
// party      - het feestje, zoals iemand het met Shift+8 aanzet
export const labScenarios = [
  {
    id: "sale",
    name: "Normale verkoop",
    steps: "verkoop",
    sales: [{ name: "Hans", amount: 450, service: "Werkplekbeheer" }],
  },
  {
    id: "big",
    name: "Grote deal",
    steps: "verkoop + extra confetti",
    sales: [{ name: "Bobbie", amount: 1650, service: "Firewall as a Service" }],
  },
  {
    id: "two",
    name: "Twee verkopen tegelijk",
    steps: "verkoop + verkoop",
    sales: [
      { name: "Bert", amount: 1100, service: "VoIP telefonie" },
      { name: "Hans", amount: 450, service: "Werkplekbeheer" },
    ],
  },
  {
    id: "climb",
    name: "Stijger",
    steps: "verkoop + stijger",
    sales: [{ name: "Johan Derksen", amount: 1200, service: "Security awareness" }],
  },
  {
    id: "podium",
    name: "Podiumwissel",
    steps: "verkoop + stijger + podium",
    sales: [{ name: "Bert", amount: 3600, service: "Werkplekbeheer" }],
  },
  {
    id: "best",
    name: "Eigen record",
    steps: "verkoop + eigen record",
    sales: [{ name: "Ernst", amount: 2950, service: "Managed Cloud Backup" }],
  },
  {
    id: "record",
    name: "Record",
    steps: "verkoop + record",
    sales: [{ name: "Ernst", amount: 5600, service: "Microsoft 365 Business" }],
  },
  {
    id: "record-climb",
    name: "Record en stijger",
    steps: "verkoop + record + podium",
    sales: [{ name: "Ernie", amount: 6400, service: "Managed Cloud Backup" }],
  },
  {
    id: "target",
    name: "Verkoop haalt target",
    steps: "verkoop + target",
    key: "0",
    nearTarget: true,
    sales: [{ name: "Hans", amount: 950, service: "Hosting & domeinen" }],
  },
  {
    id: "record-target",
    name: "Record haalt target",
    steps: "verkoop + record + target",
    nearTarget: true,
    sales: [{ name: "Ernst", amount: 5600, service: "Microsoft 365 Business" }],
  },
  {
    id: "missed",
    name: "Tv gaat aan",
    steps: "gemiste verkopen",
    animation: "missed-sales",
  },
  {
    id: "party",
    name: "Feest aangezet",
    steps: "feest",
    key: "P",
    party: true,
  },
];

// Een willekeurige verkoop, zoals er de hele dag door binnenkomen.
export function randomSale(data, { min = 250, max = 2200 } = {}) {
  return {
    name: pick(data.rankList).name,
    service: pick(data.soldServices).name,
    amount: roundDeal(min, max),
  };
}

// Wat er bij deze verkoper nog bij moet om de plek erboven te pakken.
// Staat er niemand boven, dan is er niets in te halen.
function jumpOver(data, index) {
  const climber = data.rankList[index];
  const above = data.rankList[index - 1];
  if (!climber || !above) return null;

  const gap = (above.totalPrice || 0) - (climber.totalPrice || 0);
  return {
    name: climber.name,
    service: pick(data.soldServices).name,
    amount: Math.max(50, Math.round((gap + roundDeal(300, 900)) / 50) * 50),
    from: index + 1,
    position: index,
  };
}

// Per animatie: wat er met de cijfers gebeurt en wat de animatie te zien
// krijgt. Geeft { data, payload, sale } terug - sale is puur om in het lab
// te tonen wat er zojuist veranderd is.
export function scenarioFor(id, data) {
  const nothing = { data, payload: {}, sale: null };

  switch (id) {
    // Een gewone verkoop.
    case "sale-charge":
    case "sale-splash":
    case "row-flash":
    case "gauge-pulse":
    case "screen-glow": {
      const sale = randomSale(data, { min: 250, max: 1800 });
      return { data: applySale(data, sale), payload: sale, sale };
    }

    // Confetti hoort bij elke verkoop, en bij een grotere deal komen er buien
    // overheen. Hier pakken we meteen zo'n grote, want dat is wat je wil zien.
    case "confetti-burst": {
      const sale = randomSale(data, { min: bigDealAmount, max: 4500 });
      return { data: applySale(data, sale), payload: {}, sale };
    }

    // Record: iemand uit de middenmoot zet de grootste verkoop ooit neer.
    case "fireworks": {
      const middle = Math.min(3, Math.max(1, data.rankList.length - 2));
      const seller = data.rankList[middle] || data.rankList[0];
      const sale = {
        name: seller.name,
        service: pick(data.soldServices).name,
        amount: roundDeal(3000, 6000),
      };
      // Het oude record ligt er net onder, anders was het geen record.
      const previous = Math.round((sale.amount * 0.85) / 50) * 50;
      return {
        data: applySale(data, sale),
        payload: { ...sale, previous },
        sale,
      };
    }

    // Eigen record: iemand zet zijn eigen beste verkoop neer. Kleiner dan
    // het record van het hele bord, want dat is precies het verschil.
    case "personal-best": {
      const seller = pick(data.rankList);
      const sale = {
        name: seller.name,
        service: pick(data.soldServices).name,
        amount: roundDeal(1200, 2600),
      };
      // Zijn oude beste ligt er een eind onder, anders viel er niets te
      // verbeteren.
      const previous = Math.round((sale.amount * 0.7) / 50) * 50;
      return {
        data: applySale(data, sale),
        payload: { ...sale, previous },
        sale,
      };
    }

    // Iemand van onderaf schuift een plek op.
    case "rank-up": {
      const index = Math.min(
        data.rankList.length - 1,
        3 + Math.floor(Math.random() * 2),
      );
      const jump = jumpOver(data, index);
      if (!jump) return nothing;
      return {
        data: applySale(data, jump),
        payload: { name: jump.name, position: jump.position, from: jump.from },
        sale: jump,
      };
    }

    // De top 3 wisselt van volgorde.
    case "podium-bounce": {
      const jump = jumpOver(data, 1 + Math.floor(Math.random() * 2));
      if (!jump) return nothing;
      return { data: applySale(data, jump), payload: {}, sale: jump };
    }

    // Het laatste zetje naar de 100%.
    case "target-hit": {
      const target = data.teamTarget.amount || 0;
      const missing = Math.max(500, target - data.amount);
      const sale = {
        name: pick(data.rankList).name,
        service: pick(data.soldServices).name,
        amount: missing,
      };
      const next = applySale(data, sale);
      return { data: next, payload: { amount: next.amount }, sale };
    }

    // Na het aanzetten van de tv: wat er is verkocht terwijl hij uit stond.
    // "Uit" is in het lab niet na te spelen, dus hier met de vaste
    // voorbeeldcijfers uit de registry; de cijfers op de kaarten blijven
    // staan.
    case "missed-sales":
    case "missed-nothing":
      return { data, payload: byId(id).demo, sale: null };

    default:
      return nothing;
  }
}
